import { useEffect, useId, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import styles from "./FloatingInput.module.css";
import FieldHint from "./FieldHint";
import { lookupCommunes, formatCommuneLabel, type Commune } from "@/lib/lookupCommunes";
import { validatePostalCode, extractCodePostal } from "@/lib/formValidation";

interface CommuneComboboxProps {
  /** Code postal courant, tenu par le funnel. */
  codePostal: string;
  /** Commune retenue, tenue par le funnel. Vide tant que rien n'est choisi. */
  ville: string;
  /** Remonte les deux valeurs. `ville` vaut "" tant qu'aucune commune n'est choisie. */
  onChange: (codePostal: string, ville: string) => void;
  /** Nombre de communes proposées ; `null` pendant le chargement (cf. fenêtre de course). */
  onCommunesLoaded: (count: number | null) => void;
  error?: string;
  hint?: string;
}

const NOT_FOUND_MESSAGE = "Commune introuvable pour ce code postal. Vous pouvez continuer.";

/**
 * Champ unique « code postal + commune » à autocomplétion.
 *
 * Le code postal seul ne suffit pas à identifier une commune : 3 131 des 4 699
 * codes postaux du réseau en couvrent plusieurs. Faire déclarer la commune par
 * le client est le seul moyen d'obtenir la bonne — c'est pour ça que ce champ
 * existe. Après sélection, le champ porte le libellé complet
 * (« 13100 - Aix-en-Provence ») : c'est la confirmation visuelle de
 * l'appariement code postal / commune.
 *
 * Le texte affiché est tenu en état local et reconstruit au montage depuis
 * les props, pour que le client retrouve sa saisie intacte après un retour en
 * arrière dans le funnel.
 */
export default function CommuneCombobox({
  codePostal,
  ville,
  onChange,
  onCommunesLoaded,
  error,
  hint,
}: CommuneComboboxProps) {
  const [text, setText] = useState(() => (ville ? `${codePostal} - ${ville}` : codePostal));
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Libellé + commune "confirmés" par le texte actuel : tant que le texte
  // affiché n'a pas bougé depuis une sélection, celle-ci reste valable.
  const confirmedRef = useRef<{ label: string; ville: string } | null>(
    ville ? { label: `${codePostal} - ${ville}`, ville } : null,
  );

  const listboxId = useId();
  const cp = extractCodePostal(text);

  useEffect(() => {
    if (!validatePostalCode(cp)) {
      setCommunes([]);
      setNotFound(false);
      setOpen(false);
      setActiveIndex(-1);
      onCommunesLoaded(0);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setOpen(true);
    onCommunesLoaded(null);

    lookupCommunes(cp, "national", controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return;
        setCommunes(list);
        setNotFound(list.length === 0);
        setActiveIndex(-1);
        onCommunesLoaded(list.length);

        if (list.length === 1) {
          // Auto-sélection : sur un code postal non ambigu, aucun clic imposé.
          const [commune] = list;
          const label = formatCommuneLabel(commune);
          confirmedRef.current = { label, ville: commune.nom };
          setText(label);
          setOpen(false);
          onChange(cp, commune.nom);
        } else if (list.some((c) => c.nom === confirmedRef.current?.ville)) {
          // La commune confirmée (retour en arrière) fait toujours partie de
          // la liste pour ce code postal : rien à changer, pas besoin de
          // rouvrir la liste sur une saisie qui est déjà la bonne.
          setOpen(false);
        } else {
          // Code postal nouveau ou modifié : la commune précédente (s'il y en
          // avait une) n'est plus valable.
          confirmedRef.current = null;
          onChange(cp, "");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // Volontairement dépendant du seul code postal : re-déclencher sur
    // `onChange`/`onCommunesLoaded` relancerait le fetch à chaque sélection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cp]);

  function selectCommune(commune: Commune) {
    const label = formatCommuneLabel(commune);
    confirmedRef.current = { label, ville: commune.nom };
    setText(label);
    setOpen(false);
    setActiveIndex(-1);
    onChange(cp, commune.nom);
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const newText = e.target.value;
    const previousCp = cp;
    setText(newText);
    setActiveIndex(-1);

    if (confirmedRef.current?.label === newText) {
      // Le texte correspond exactement à la dernière sélection : rien à
      // invalider.
      return;
    }

    // Frappe après une sélection (ou saisie neuve) : la commune est
    // invalidée tant qu'elle n'a pas été reconfirmée.
    confirmedRef.current = null;
    const newCp = extractCodePostal(newText);
    onChange(newCp, "");

    if (validatePostalCode(newCp) && newCp === previousCp) {
      // Même code postal que la liste déjà chargée : pas besoin de refetch,
      // on rouvre simplement la liste existante.
      setOpen(true);
    }
    // Sinon, c'est l'effet ci-dessus (dépendant de `cp`) qui gère la suite :
    // fermeture si incomplet, fetch + ouverture si un nouveau CP est atteint.
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      if (communes.length === 0) return;
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1 >= communes.length ? 0 : i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      if (communes.length === 0) return;
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i - 1 < 0 ? communes.length - 1 : i - 1));
      return;
    }
    if (e.key === "Enter") {
      if (open && activeIndex >= 0 && communes[activeIndex]) {
        e.preventDefault();
        selectCommune(communes[activeIndex]);
      }
    }
  }

  const showList = open && !loading && communes.length > 0;
  const showNotFound = open && !loading && notFound;
  const activeOptionId =
    showList && activeIndex >= 0 && communes[activeIndex]
      ? `${listboxId}-option-${activeIndex}`
      : undefined;
  const hintId = hint ? "commune-hint" : undefined;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor="commune">
        Code postal et commune *
      </label>
      {hint && <FieldHint id={hintId}>{hint}</FieldHint>}
      {/* Ancre la liste sur l'input seul (et non sur .field, qui englobe
          aussi le message d'erreur) : sinon `top: 100%` se cale sous le
          message plutôt que sous l'input dès qu'une erreur est affichée. */}
      <div style={{ position: "relative" }}>
        <input
          id="commune"
          name="commune"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={showList ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          aria-describedby={hintId}
          className={`${styles.input} ${error ? styles.hasError : ""}`}
          value={text}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (communes.length > 0 || notFound) setOpen(true);
          }}
          onBlur={() => setOpen(false)}
        />
        {showList && (
          <ul
            id={listboxId}
            role="listbox"
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 10,
              margin: 0,
              padding: 0,
              listStyle: "none",
              maxHeight: 220,
              overflowY: "auto",
              background: "var(--color-white)",
              border: "1px solid var(--color-gray-input)",
              borderTop: "none",
            }}
          >
            {communes.map((c, i) => (
              <li
                key={`${c.code_insee}-${c.code_postal ?? ""}`}
                id={`${listboxId}-option-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => {
                  // Empêche le blur de l'input de se produire avant le click :
                  // sans ce preventDefault, le blur ferme la liste avant que
                  // le clic n'atteigne l'option.
                  e.preventDefault();
                }}
                onClick={() => selectCommune(c)}
                onMouseEnter={() => setActiveIndex(i)}
                style={{
                  padding: "8px 10px",
                  cursor: "pointer",
                  background: i === activeIndex ? "var(--color-gray-light, #f0f0f0)" : "transparent",
                }}
              >
                {formatCommuneLabel(c)}
              </li>
            ))}
          </ul>
        )}
      </div>
      {loading && (
        <span style={{ fontSize: 13, marginTop: 4, opacity: 0.7 }}>
          Chargement des communes…
        </span>
      )}
      {showNotFound && <span className={styles.errorLabel}>{NOT_FOUND_MESSAGE}</span>}
      {error && <span className={styles.errorLabel}>{error}</span>}
    </div>
  );
}
